import {
    addDocument,
    getCollectionDocuments,
    queryDocuments,
    setDocument,
    updateDocument,
    getDocumentById
} from "../firebase/firestore";

export const learningService = {
    async createLearningPath(data) {
        return await addDocument("learningPaths", {
            name: data.name,
            description: data.description,
            difficulty: data.difficulty,
            creditsOnCompletion: data.creditsOnCompletion || 10,
            icon: data.icon || "book",
            color: data.color || "#667eea",
            duration: data.duration || "4 weeks",
            levels: data.levels || [],
            active: true,
            createdAt: new Date(),
            updatedAt: new Date()
        });
    },

    async getLearningPaths(active = true) {
        if (active) {
            return await queryDocuments("learningPaths", [["active", "==", true]]);
        }
        return await getCollectionDocuments("learningPaths");
    },

    async getLearningPathById(pathId) {
        return await getDocumentById("learningPaths", pathId);
    },

    async updateLearningPath(pathId, data) {
        await updateDocument("learningPaths", pathId, {
            ...data,
            updatedAt: new Date()
        });
    },

    async createLevel(pathId, data) {
        const path = await this.getLearningPathById(pathId);
        const levels = path?.levels || [];
        const newLevel = {
            id: `level_${Date.now()}`,
            pathId,
            name: data.name,
            description: data.description,
            order: data.order || levels.length + 1,
            testId: data.testId,
            questions: data.questions || [],
            qualifyingScore: data.qualifyingScore || 70,
            duration: data.duration || "30 mins",
            active: true,
            createdAt: new Date()
        };

        levels.push(newLevel);
        await updateDocument("learningPaths", pathId, { levels });
        return newLevel.id;
    },

    async updateLevel(pathId, levelId, data) {
        const path = await this.getLearningPathById(pathId);
        const levels = path?.levels || [];
        const levelIndex = levels.findIndex((l) => l.id === levelId);

        if (levelIndex !== -1) {
            levels[levelIndex] = {
                ...levels[levelIndex],
                ...data,
                updatedAt: new Date()
            };
            await updateDocument("learningPaths", pathId, { levels });
        }
    },

    async deleteLevel(pathId, levelId) {
        const path = await this.getLearningPathById(pathId);
        const levels = (path?.levels || []).filter((l) => l.id !== levelId);
        await updateDocument("learningPaths", pathId, { levels });
    },

    async getLevelsByPath(pathId) {
        const path = await this.getLearningPathById(pathId);
        return (path?.levels || []).sort((a, b) => a.order - b.order);
    },

    async getLevelById(pathId, levelId) {
        const path = await this.getLearningPathById(pathId);
        return (path?.levels || []).find((l) => l.id === levelId);
    },

    async createStudentProgress(userId, pathId) {
        const progressId = `${userId}_${pathId}`;
        return await setDocument("studentProgress", progressId, {
            userId,
            pathId,
            currentLevel: 0,
            levelStatus: {},
            totalCreditsEarned: 0,
            pathCompleted: false,
            completedAt: null,
            createdAt: new Date(),
            updatedAt: new Date()
        });
    },

    async getStudentProgress(userId, pathId) {
        const progressId = `${userId}_${pathId}`;
        return await getDocumentById("studentProgress", progressId);
    },

    async updateLevelAttempt(userId, pathId, levelId, score, qualifyingScore) {
        const progressId = `${userId}_${pathId}`;
        let progress = await this.getStudentProgress(userId, pathId);

        if (!progress) {
            await this.createStudentProgress(userId, pathId);
            progress = await this.getStudentProgress(userId, pathId);
        }

        const levelStatus = progress?.levelStatus || {};
        const currentStatus = levelStatus[levelId] || {
            status: "locked",
            attempts: 0,
            lastAttemptDate: null,
            highestScore: 0,
            cleared: false
        };

        const isPassed = score >= qualifyingScore;
        const newStatus = {
            ...currentStatus,
            attempts: currentStatus.attempts + 1,
            lastAttemptDate: new Date(),
            highestScore: Math.max(currentStatus.highestScore, score),
            cleared: isPassed,
            status: isPassed ? "cleared" : "active"
        };

        levelStatus[levelId] = newStatus;

        const path = await this.getLearningPathById(pathId);
        const levels = path?.levels || [];
        const clearedLevels = Object.values(levelStatus).filter((s) => s.cleared).length;
        const isPathCompleted = clearedLevels === levels.length;

        let creditsEarned = 0;
        if (isPassed && !currentStatus.cleared) {
            creditsEarned = path?.creditsOnCompletion || 10;
        }

        await updateDocument("studentProgress", progressId, {
            levelStatus,
            currentLevel: clearedLevels,
            totalCreditsEarned: (progress?.totalCreditsEarned || 0) + creditsEarned,
            pathCompleted: isPathCompleted,
            completedAt: isPathCompleted ? new Date() : null
        });

        return {
            passed: isPassed,
            creditsEarned,
            status: newStatus,
            pathCompleted: isPathCompleted
        };
    },

    async canAttemptLevel(userId, pathId, levelId) {
        const progress = await this.getStudentProgress(userId, pathId);
        const levelStatus = progress?.levelStatus?.[levelId];

        if (!levelStatus) {
            return { canAttempt: true, reason: null };
        }

        if (levelStatus.cleared) {
            return { canAttempt: false, reason: "Level already cleared" };
        }

        if (levelStatus.attempts > 0 && levelStatus.lastAttemptDate) {
            const lastAttempt = new Date(levelStatus.lastAttemptDate);
            const now = new Date();
            const daysSinceLastAttempt = (now - lastAttempt) / (1000 * 60 * 60 * 24);

            if (daysSinceLastAttempt < 7) {
                const daysRemaining = Math.ceil(7 - daysSinceLastAttempt);
                return {
                    canAttempt: false,
                    reason: `Please wait ${daysRemaining} days before retrying`
                };
            }
        }

        return { canAttempt: true, reason: null };
    },

    async getStudentProgressSummary(userId) {
        const progresses = await queryDocuments("studentProgress", [["userId", "==", userId]]);
        return progresses;
    },

    async getPathProgressList(pathId) {
        const progresses = await queryDocuments("studentProgress", [["pathId", "==", pathId]]);

        const enrichedProgresses = await Promise.all(
            progresses.map(async (p) => {
                const user = await getDocumentById("users", p.userId);
                return {
                    ...p,
                    userName: `${user?.firstName} ${user?.lastName}`,
                    userEmail: user?.email
                };
            })
        );

        return enrichedProgresses;
    },

    async getPathStats(pathId) {
        const progresses = await queryDocuments("studentProgress", [["pathId", "==", pathId]]);
        const completedCount = progresses.filter((p) => p.pathCompleted).length;
        const totalEnrolled = progresses.length;
        const avgCredits = progresses.length > 0
            ? progresses.reduce((sum, p) => sum + (p.totalCreditsEarned || 0), 0) / progresses.length
            : 0;

        return {
            totalEnrolled,
            completedCount,
            completionRate: totalEnrolled > 0 ? (completedCount / totalEnrolled) * 100 : 0,
            avgCreditsEarned: avgCredits
        };
    }
};

export default learningService;
